import React, { useMemo, useCallback } from 'react';
import { QueuedVideo } from '@/utils/optimistic/optimisticGenerate';
import { type UserImage } from '@/api/imageApi';
import { UploadingVideo } from '@/utils/optimistic/optimisticUpload';
import { ImageRowSkeleton } from '@/components/ui/skeleton/Skeleton';
import { ImageTile, QueuedImageTile, VirtualizedGrid } from '@/components/ui/common';
import { useResponsiveColumns, IMAGE_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns';
import UploadingImageTile from './UploadingImageTile';
import { 
  renderImagesLoadingState,
  renderImagesErrorState,
  renderImagesEmptyState 
} from './ImagesGalleryHelpers';

interface VirtualizedImagesSectionProps {
  filteredImages: UserImage[];
  queuedImages: QueuedVideo[];
  uploadingImages?: UploadingVideo[];
  isLoadingImages: boolean;
  isLoadingMoreImages?: boolean;
  imagesError: string | null;
  onRetryImages?: () => void;
  handleCancel?: () => void;
  cancelQueuedVideo?: (id: string) => void;
  handleImageClick?: (image: UserImage) => void;
  onAttachImage?: (image: UserImage, tab: 'Videos' | 'Images') => void;
  onFavouriteSuccess?: (isFavourited: boolean) => void;
  onArchiveSuccess?: (isArchived: boolean) => void;
  isAdmin?: boolean;
}

/**
 * Virtualized images section component
 * Handles large image galleries with smooth performance
 * 
 * This component maintains the exact same UI as the original renderImagesSection
 * but uses virtualization to only render visible images
 * 
 * Uses shared components:
 * - ImageTile from ui/common for consistent image rendering
 * - ImageRowSkeleton from ui/skeleton for loading states
 */
const VirtualizedImagesSection: React.FC<VirtualizedImagesSectionProps> = ({
  filteredImages,
  queuedImages,
  uploadingImages = [],
  isLoadingImages,
  isLoadingMoreImages = false,
  imagesError,
  onRetryImages,
  handleCancel,
  cancelQueuedVideo,
  handleImageClick,
  onAttachImage,
  onFavouriteSuccess,
  onArchiveSuccess,
  isAdmin = false
}) => {
  const columns = useResponsiveColumns(IMAGE_GRID_COLUMNS);

  const imageItems = filteredImages || [];

  // Combine all items into a unified list for grid layout
  // Order: queued (generating) -> uploading -> regular images
  type GridItem = 
    | { type: 'uploading'; data: UploadingVideo }
    | { type: 'queued'; data: QueuedVideo }
    | { type: 'image'; data: UserImage };

  const allItems = useMemo<GridItem[]>(() => {
    const items: GridItem[] = [];
    
    // Add queued/generating images first
    queuedImages.forEach(img => items.push({ type: 'queued', data: img }));
    
    // Add uploading images
    uploadingImages.forEach(img => items.push({ type: 'uploading', data: img }));
    
    // Add regular images
    imageItems.forEach(img => items.push({ type: 'image', data: img }));
    
    return items;
  }, [uploadingImages, queuedImages, imageItems]);

  const hasAnyImages = allItems.length > 0;

  // Estimate row height (1:1 aspect ratio cards + gap)
  const estimatedRowHeight = 340; // ~300px for 1:1 card + 32px gap + padding

  // Render function for each grid item
  const renderItem = useCallback((item: GridItem) => {
    if (item.type === 'uploading') {
      return (
        <UploadingImageTile
          uploadingImage={item.data}
        />
      );
    }
    if (item.type === 'queued') {
      return (
        <QueuedImageTile
          queuedImage={item.data}
          handleCancel={handleCancel}
          cancelQueuedVideo={cancelQueuedVideo}
        />
      );
    }
    // Regular image
    return (
      <ImageTile
        image={item.data}
        onClick={handleImageClick}
        onAttach={onAttachImage}
        isAdmin={isAdmin}
        onFavouriteSuccess={onFavouriteSuccess}
        onArchiveSuccess={onArchiveSuccess}
      />
    );
  }, [handleCancel, cancelQueuedVideo, handleImageClick, onAttachImage, onFavouriteSuccess, onArchiveSuccess, isAdmin]);

  // Get unique key for each item
  const getItemKey = useCallback((item: GridItem) => {
    if (item.type === 'uploading') {
      return `uploading-${item.data.id}`;
    }
    if (item.type === 'queued') {
      return `queued-${item.data.id}`;
    }
    return `${item.data.id}-${item.data.status}-${item.data.updated_at}`;
  }, []);

  // Show loading state for images
  if (isLoadingImages && !hasAnyImages) {
    return renderImagesLoadingState();
  }

  // Show error state for images
  if (imagesError && !isLoadingImages) {
    return renderImagesErrorState(imagesError, onRetryImages);
  }

  // Show empty state - no images at all
  if (!hasAnyImages) {
    return renderImagesEmptyState();
  }

  // Show content when we have images or queued images
  return (
    <div>
      {/* Virtualized Grid - All items combined */}
      <VirtualizedGrid
        items={allItems}
        columns={columns}
        estimatedRowHeight={estimatedRowHeight}
        emptyAspectRatio="1/1"
        renderItem={renderItem}
        getItemKey={getItemKey}
      />

      {/* Loading more indicator */}
      {isLoadingMoreImages && (
        <div className="mt-4">
          <ImageRowSkeleton columns={columns} />
        </div>
      )}
    </div>
  );
};

export default VirtualizedImagesSection;
